import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Calendar, LogOut, Loader2, User, Star, Trophy, Award } from 'lucide-react';
import OrdersList from '@/components/account/OrdersList';
import BookingsList from '@/components/account/BookingsList';
import ProfileDetails from '@/components/account/ProfileDetails';
import LoginForm from '@/components/account/LoginForm';
import RegisterForm from '@/components/account/RegisterForm';
import SignInForm from '@/components/account/SignInForm';
import { useAuth } from '@/lib/AuthContext';
import DriverBenefits from '@/components/account/DriverBenefits';
import SessionResults from '@/components/account/SessionResults';
import LoyaltyOverview from '@/components/account/LoyaltyOverview';
import LoyaltyPointsOverview from '@/components/account/LoyaltyPointsOverview';
import TierAvatar from '@/components/account/TierAvatar';

export default function Account() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('orders');
  const [view, setView] = useState('login'); // 'login' | 'signin' | 'register'
  const [registered, setRegistered] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkUserAuth } = useAuth();

  // If a B2B trade-account invite token was stashed before registration or
  // sign-in, send the now-authenticated user back to the trade portal so the
  // invite is accepted automatically. Returns true when it redirects.
  const consumeB2bInvite = () => {
    try {
      const t = localStorage.getItem('b2b_invite_token');
      if (t) { navigate(`/b2b?invite=${encodeURIComponent(t)}`); return true; }
    } catch (_e) { /* localStorage unavailable */ }
    return false;
  };

  const { data: sessionResults = [] } = useQuery({
    queryKey: ['session-results', user?.email],
    queryFn: () => base44.entities.SessionResult.filter({ customer_email: user.email }, '-session_date'),
    enabled: !!user?.email,
  });
  const resultsCount = sessionResults.length;

  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'register' || searchParams.get('invite') === 'admin') setView('register');
    else if (mode === 'signin') setView('signin');
    base44.auth.isAuthenticated().then(async (authed) => {
      if (authed) {
        const me = await base44.auth.me();
        setUser(me);
      }
      setLoading(false);
    });
  }, [searchParams]);

  const handleLogin = () => {
    setView('signin');
  };

  const handleLogout = () => {
    base44.auth.logout('/account');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    if (view === 'register') {
      return (
        <RegisterForm
          initialEmail={searchParams.get('email') || ''}
          onBack={() => setView('login')}
          onSuccess={async (fullName) => {
            await checkUserAuth();
            const me = await base44.auth.me();
            if (consumeB2bInvite()) return;
            if (me.role === 'admin') { navigate('/admin/dashboard'); return; }
            setUser({ ...me, full_name: fullName || me.full_name });
          }}
        />
      );
    }
    if (view === 'signin') {
      return (
        <SignInForm
          onBack={() => setView('login')}
          onSuccess={async () => {
            await checkUserAuth();
            const me = await base44.auth.me();
            if (consumeB2bInvite()) return;
            if (me.role === 'admin') { navigate('/admin/dashboard'); return; }
            setUser(me);
          }}
        />
      );
    }
    return <LoginForm onLogin={handleLogin} onRegister={() => setView('register')} registered={registered} />;
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <TierAvatar email={user.email} profilePictureUrl={user.profile_picture_url} size={72} />
            <div>
            <p className="text-primary text-xs font-bold tracking-widest mb-2">MY ACCOUNT</p>
            <h1 className="text-white font-black text-3xl md:text-4xl uppercase">
              {(() => {
                const emailLocal = (user.email || '').split('@')[0].toLowerCase();
                const fn = (user.first_name || '').trim();
                const raw = fn || (user.full_name && user.full_name.toLowerCase() !== emailLocal ? user.full_name.split(' ')[0] : '');
                return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Welcome Back';
              })()}
            </h1>
            <p className="text-white/30 text-sm mt-1">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/review"
              className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-xs font-bold tracking-wider px-4 py-2.5 transition-all"
            >
              <Star className="w-4 h-4" /> LEAVE A REVIEW
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 border border-white/10 hover:border-white/25 text-white/40 hover:text-white text-xs font-bold tracking-wider px-4 py-2.5 transition-all"
            >
              <LogOut className="w-4 h-4" /> SIGN OUT
            </button>
          </div>
        </div>
      </section>

      <DriverBenefits email={user.email} />

      {/* Tabs */}
      <div className="border-b border-white/5 bg-[#0d0d0f] sticky top-[var(--header-offset)] z-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 flex">
          {[
            { key: 'orders', label: 'MY ORDERS', icon: Package },
            { key: 'bookings', label: 'MY BOOKINGS', icon: Calendar },
            { key: 'results', label: 'MY RESULTS', icon: Trophy },
            { key: 'loyalty', label: 'LOYALTY', icon: Award },
            { key: 'details', label: 'MY DETAILS', icon: User },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 py-4 px-1 mr-8 text-xs font-bold tracking-wider border-b-2 transition-all ${
                tab === key ? 'border-primary text-primary' : 'border-transparent text-white/30 hover:text-white/60'
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
              {key === 'results' && resultsCount > 0 && (
                <span className="bg-primary text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1.5 flex items-center justify-center">{resultsCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <section className="px-6 lg:px-10 py-10 max-w-4xl mx-auto">
        <AnimatePresence mode="wait">
          {tab === 'orders' && (
            <motion.div key="orders" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <OrdersList email={user.email} />
            </motion.div>
          )}
          {tab === 'bookings' && (
            <motion.div key="bookings" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <BookingsList email={user.email} />
            </motion.div>
          )}
          {tab === 'results' && (
            <motion.div key="results" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <SessionResults email={user.email} />
            </motion.div>
          )}
          {tab === 'loyalty' && (
            <motion.div key="loyalty" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <LoyaltyPointsOverview email={user.email} />
              <div className="mt-8 pt-8 border-t border-white/5">
                <LoyaltyOverview email={user.email} />
              </div>
            </motion.div>
          )}
          {tab === 'details' && (
            <motion.div key="details" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <ProfileDetails user={user} onUserUpdate={setUser} />
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
}